import { Image as ExpoImage } from 'expo-image';
import { ExternalLink, Globe } from 'lucide-react-native';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { openTextLink } from './link-utils';
import type { LinkPreview } from './post-service';

export function shouldUseFeaturedLinkPreview(preview: LinkPreview | null) {
  if (!preview?.imageURL || !preview.imageWidth || !preview.imageHeight) {
    return false;
  }

  const aspectRatio = preview.imageWidth / preview.imageHeight;

  return (
    preview.imageWidth >= 600 &&
    preview.imageHeight >= 300 &&
    aspectRatio >= 1.45 &&
    aspectRatio <= 2.25
  );
}

export function LinkPreviewCard({
  compact = false,
  preview,
  source,
}: {
  compact?: boolean;
  preview: LinkPreview | null;
  source: string;
}) {
  if (!preview) {
    return null;
  }

  const isReady = preview.status === 'ready';
  const title = isReady
    ? preview.title || preview.displayUrl
    : preview.displayUrl;
  const useFeaturedLayout =
    isReady && !compact && shouldUseFeaturedLinkPreview(preview);

  if (useFeaturedLayout && preview.imageURL) {
    return (
      <Pressable
        accessibilityLabel={`Open ${title}`}
        accessibilityRole="button"
        onPress={(event) => {
          event.stopPropagation();
          void openTextLink({ source, url: preview.url });
        }}
        style={({ pressed }) => [
          styles.featuredCard,
          pressed ? styles.cardPressed : null,
        ]}
      >
        <View style={styles.featuredImageFrame}>
          <ExpoImage
            accessibilityLabel=""
            cachePolicy="memory-disk"
            contentFit="cover"
            source={{ uri: preview.imageURL }}
            style={styles.image}
          />
        </View>
        <View style={styles.featuredTextBlock}>
          <Text numberOfLines={1} style={styles.featuredDomain}>
            {preview.siteName || preview.displayUrl}
          </Text>
          <Text numberOfLines={2} style={styles.featuredTitle}>
            {title}
          </Text>
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityLabel={`Open ${title}`}
      accessibilityRole="button"
      onPress={(event) => {
        event.stopPropagation();
        void openTextLink({ source, url: preview.url });
      }}
      style={({ pressed }) => [
        styles.card,
        compact ? styles.compactCard : null,
        pressed ? styles.cardPressed : null,
      ]}
    >
      <View
        style={[styles.imageFrame, compact ? styles.compactImageFrame : null]}
      >
        {preview.imageURL ? (
          <ExpoImage
            accessibilityLabel=""
            cachePolicy="memory-disk"
            contentFit="cover"
            source={{ uri: preview.imageURL }}
            style={styles.image}
          />
        ) : (
          <View style={styles.placeholder}>
            <Globe color={colors.secondary} size={compact ? 20 : 26} />
          </View>
        )}
      </View>
      <View style={styles.textColumn}>
        <View style={styles.domainRow}>
          <Text numberOfLines={1} style={styles.domain}>
            {preview.siteName || preview.displayUrl}
          </Text>
          <ExternalLink color={colors.mutedText} size={compact ? 12 : 14} />
        </View>
        <Text
          numberOfLines={2}
          style={[styles.title, compact ? styles.compactTitle : null]}
        >
          {title}
        </Text>
        {preview.description ? (
          <Text numberOfLines={compact ? 1 : 2} style={styles.description}>
            {preview.description}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    alignItems: 'stretch',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.md,
    overflow: 'hidden',
  },
  cardPressed: {
    opacity: 0.82,
  },
  compactCard: {
    borderRadius: 12,
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  compactImageFrame: {
    width: 64,
  },
  compactTitle: {
    fontSize: 13,
  },
  description: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
    marginTop: 3,
  },
  domain: {
    color: colors.mutedText,
    flex: 1,
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  domainRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 5,
    minWidth: 0,
  },
  featuredCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    marginTop: spacing.md,
    overflow: 'hidden',
  },
  featuredDomain: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  featuredImageFrame: {
    aspectRatio: 1.91,
    backgroundColor: colors.background,
    width: '100%',
  },
  featuredTextBlock: {
    backgroundColor: colors.surfaceTint,
    gap: 6,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  featuredTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
    lineHeight: 22,
  },
  image: {
    height: '100%',
    width: '100%',
  },
  imageFrame: {
    aspectRatio: 1,
    backgroundColor: colors.background,
    width: 92,
  },
  placeholder: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    flex: 1,
    justifyContent: 'center',
  },
  textColumn: {
    flex: 1,
    justifyContent: 'center',
    minWidth: 0,
    paddingBottom: spacing.sm,
    paddingRight: spacing.md,
    paddingTop: spacing.sm,
  },
  title: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 20,
    marginTop: 4,
  },
});
